import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Checkbox } from "@amzn/stencil-react-components/checkbox";
import { Col, Container, Row } from "@amzn/stencil-react-components/layout";
import { Link } from "@amzn/stencil-react-components/link";
import { MessageBanner } from "@amzn/stencil-react-components/message-banner";
import { ModalContent, WithModal } from "@amzn/stencil-react-components/modal";
import { Spinner } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import IconExternalLink from "@amzn/stencil-react-icons/icons/icon-external-link";
import { TrafficFrom } from "common/helpers/traffic-from";
import { AMAZONIAN_ORDER_LABEL, CatalogItemDomains, CreateOrderRequestContent } from "common/types";
import { getString } from "common/uistringlabels/uiStringUtils";
import { GENERAL_PHONE_NUMBER_REGEX } from "config";
import { AmazonianInformation } from "lib/models/oui-models";
import { IOrdersService, OrdersServiceFactory, UserPermissions } from "lib/services";
import { CSRFService } from "lib/services/csrf-service";
import { UserInformation } from "lib/services/user-information-service";
import { ItemTaxonomyId } from "lib/taxonomy/item-taxonomy-id";
import React, { FC, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useChangeAddressFormActions, useChangeAddressFormItems } from "view/Page/CheckoutPage/useChangeAddressForm";
import { useCheckoutFormActions, useCheckoutFormItems } from "view/Page/CheckoutPage/useCheckoutForm";
import { OrderConfirmationState } from "view/Page/OrderConfirmation/OrderConfirmation";

import ChangeShippingDetailsCard from "./Cards/ChangeShippingDetailsCard";
import GetManagerApprovalCard from "./Cards/GetManagerApprovalCard";

export interface CheckoutFormPanelProps {
  checkoutItems: useCheckoutFormItems;
  checkoutActions: useCheckoutFormActions;
  addressItems: useChangeAddressFormItems;
  addressActions: useChangeAddressFormActions;
  pdpItemTaxonomyId?: string;
}

export interface CatalogItemVersion {
  id?: string;
  price?: number;
  recurrence?: any;
  currency?: string;
  name?: string;
  shortDescription?: string;
}

const RETURN_DEVICE_COUNTRIES = ["CRI", "IND"];
const stringPath = "checkout.formPanel";
const hardwareOwnershipPolicyLink = "https://policy.a2z.com/docs/37992/publication";
const itServicesHardwarePolicy = "https://policy.a2z.com/docs/44534/publication";

const CheckoutFormPanel: FC<CheckoutFormPanelProps> = (props: CheckoutFormPanelProps) => {
  const [isDeviceReturnRequired, setisDeviceReturnRequired] = useState<boolean>(false);
  const [isLoadingGetUserInfo, setIsLoadingGetUserInfo] = useState<boolean>(true);
  const [isLoadingSubmitOrder, setIsLoadingSubmitOrder] = useState<boolean>(false);
  const [userInfo, setUserInfo] = useState<AmazonianInformation | undefined>(undefined);

  const history = useHistory();

  useEffect(() => {
    if (!userInfo) {
      getUserInfo();
    }
  }, []);

  const getUserInfo = async () => {
    const amazonianEmployeeInfo = await UserInformation.current.getUserInformation();
    setUserInfo(amazonianEmployeeInfo);
    props.addressActions.setAddressInformation(amazonianEmployeeInfo);
    setisDeviceReturnRequired(
      RETURN_DEVICE_COUNTRIES.includes(amazonianEmployeeInfo.basicInfo.addresses.home.country ?? "invalid")
    );
    setIsLoadingGetUserInfo(false);
  };

  const onExitPageClick = (closeModal: () => void) => {
    let redirectUrl = "/";
    if (props.pdpItemTaxonomyId) {
      try {
        redirectUrl = `items/${ItemTaxonomyId.Parse(props.pdpItemTaxonomyId).getUrlSuffix()}?from=${
          TrafficFrom.Checkout
        }`;
      } catch {
        /* Redirect to Home page. */
      }
    }

    history.push(redirectUrl);
    closeModal();
  };

  const validateForm: () => boolean = () => {
    // Phone number validation with regex
    if (!new RegExp(GENERAL_PHONE_NUMBER_REGEX).test(props.addressItems.formValues.phoneNumber)) {
      props.addressActions.setIsInvalidPhoneNumber(true);
      return false;
    }
    return true;
  };

  const clearValidationErrors: () => void = () => {
    props.addressActions.setIsInvalidPhoneNumber(false);
  };

  const renderCancelModal = ({ close }: { close: () => void }) => {
    return (
      <ModalContent
        data-testid="cancel-order-modal"
        titleText={getString(`${stringPath}.cancelModal.title`)}
        buttons={[
          <Button key="no-modal-button" onClick={() => close()}>
            {getString(`${stringPath}.cancelModal.noButton`)}
          </Button>,
          <Button key="yes-modal-button" variant={ButtonVariant.Primary} onClick={() => onExitPageClick(close)}>
            {getString(`${stringPath}.cancelModal.yesButton`)}
          </Button>,
        ]}
      >
        <Text fontSize="T200" color="neutral90">
          {getString(`${stringPath}.cancelModal.content`)}
        </Text>
      </ModalContent>
    );
  };

  const onSubmitClick = async () => {
    clearValidationErrors();

    if (!validateForm()) {
      return;
    }

    const request: CreateOrderRequestContent = {
      items: {
        [props.checkoutItems.formValues.catalogItemVersion.taxonomyId]: {
          quantity: 1,
          details: isDeviceReturnRequired
            ? {
                returnedDeviceSerialNumber: props.checkoutItems.formValues.serialNumberForReturnDevice,
              }
            : undefined,
        },
      },
      deliveryTo: props.checkoutActions.createDeliveryAddress(props.addressItems.formValues),
      details: {
        checkoutInfo: {
          costCenter: props.checkoutItems.formValues.costCenter,
          carCode: props.checkoutItems.formValues.carCode,
          businessJustification: props.checkoutItems.formValues.businessJustification,
        },
        deliverTo: props.checkoutActions.addCountryToDeliverToDetails(props.addressItems.formValues),
      } as any,
      acknowledgementAudit: [
        {
          by: UserPermissions.current.username!,
          on: new Date(),
          policyName: "Shipping details are accurate confirmation",
          acknowledged: props.checkoutItems.formValues.shippingDetailsConfirmationCheckbox,
        },
        {
          by: UserPermissions.current.username!,
          on: new Date(),
          policyName: "Manager is aware and information is accurate confirmation",
          acknowledged: props.checkoutItems.formValues.managerConfirmationCheckbox,
        },
        {
          by: UserPermissions.current.username!,
          on: new Date(),
          policyName: "Hardware Ownership Policy",
          acknowledged: props.checkoutItems.formValues.policyConfirmationCheckbox,
        },
        {
          by: UserPermissions.current.username!,
          on: new Date(),
          policyName: "It Services Hardware Policy",
          acknowledged: props.checkoutItems.formValues.policyConfirmationCheckbox,
        },
      ],
      labels: [AMAZONIAN_ORDER_LABEL],
    };
    await submitOrderHandler(request);
  };

  const submitOrderHandler = async (request: CreateOrderRequestContent) => {
    const ordersService: IOrdersService = OrdersServiceFactory.getInstance(
      props.checkoutItems.formValues.catalogItemVersion.taxonomyId
    );

    try {
      setIsLoadingSubmitOrder(true);
      const token = await new CSRFService().getToken();
      const response = await ordersService.CreateOrder(request, token);
      if (!response.orderId) {
        setIsLoadingSubmitOrder(false);
        throw new Error("There was an error while creating the order");
      }
      // Removes the ito:: part of the id
      const orderTaxonomyId = response.orderId.substring(5);
      history.push({
        pathname: `/confirmation/${orderTaxonomyId}`,
        state: {
          ...response,
          itemType: CatalogItemDomains.HARDWARE,
        } as OrderConfirmationState,
      });
    } catch (error: any) {
      setIsLoadingSubmitOrder(false);
    }
  };

  return (
    <Col width="100%" data-testid="checkout-form-panel">
      <Container paddingHorizontal={80} paddingTop={32} paddingBottom={32} isFullWidth={true}>
        <Col gridGap="S400" width="100%">
          {/* Title */}
          <Text fontSize="T500" fontWeight="bold" color="neutral90">
            {getString(`${stringPath}.checkout`)}
          </Text>

          {/* Banner */}
          <MessageBanner data-testid="checkout-banner" isDismissible={true}>
            {getString(`${stringPath}.banner`)}
          </MessageBanner>

          <Text fontSize="T200" color="neutral90">
            {getString(`${stringPath}.requiredField`)}
          </Text>

          {/* Step 1 - Get approval information */}
          <GetManagerApprovalCard
            {...props}
            userInfo={userInfo}
            isLoadingUserInfo={isLoadingGetUserInfo}
            isDeviceReturnRequired={isDeviceReturnRequired}
          />

          {/* Step 2 - Confirm shipping details */}
          <ChangeShippingDetailsCard {...props} userInfo={userInfo} isLoadingUserInfo={isLoadingGetUserInfo} />

          {/* Policy confirmation */}
          <Row gridGap="S200">
            <Checkbox
              data-testid="policy-checkbox"
              checked={props.checkoutItems.formValues.policyConfirmationCheckbox}
              onChange={(event) =>
                props.checkoutActions.handleInputChange("policyConfirmationCheckbox", event.target.checked)
              }
            />
            <Text fontSize="T200" color="neutral90">
              {getString(`${stringPath}.iHaveRead`)}
              <Link
                href={hardwareOwnershipPolicyLink}
                icon={<IconExternalLink title={getString(`aria.opensInNewTab`)} />}
                target="_blank"
              >
                {getString(`${stringPath}.hardwareOwnershipPolicy`)}
              </Link>
              {getString(`${stringPath}.and`)}
              <Link
                href={itServicesHardwarePolicy}
                icon={<IconExternalLink title={getString(`aria.opensInNewTab`)} />}
                target="_blank"
              >
                {getString(`${stringPath}.itServicesHardwarePolicy`)}
              </Link>
              . *
            </Text>
          </Row>

          {/* Buttons */}
          <Row gridGap="S300" display="flex" justifyContent="flex-end">
            <WithModal renderModal={renderCancelModal} isScrollable={false}>
              {({ open }) => (
                <Button data-testid="cancel-order-button" variant={ButtonVariant.Tertiary} onClick={open}>
                  {getString(`${stringPath}.cancel`)}
                </Button>
              )}
            </WithModal>
            <Button
              data-testid="submit-order-button"
              disabled={
                !props.checkoutActions.hasRequiredFields(isDeviceReturnRequired) ||
                !props.addressActions.hasRequiredFields() ||
                isLoadingSubmitOrder
              }
              variant={ButtonVariant.Primary}
              onClick={onSubmitClick}
            >
              {getString(`${stringPath}.submitOrder`)}
              {isLoadingSubmitOrder ? <Spinner /> : undefined}
            </Button>
          </Row>
        </Col>
      </Container>
    </Col>
  );
};

export default CheckoutFormPanel;
